import { Bell, FileSignature, GitMerge, Network, Scale, ScanText, Search } from "lucide-react";
import { Link } from "react-router";
import { generateMetaTags, SITE_URL } from "~/lib/seo";

export function meta() {
	return generateMetaTags({
		title: "Firemní události z listin – FirmIndex",
		description:
			"Fúze, změny základního kapitálu, nové stanovy i vztahy ve skupině — vytěžené z listin ve Sbírce listin pomocí AI, často měsíce před zápisem do obchodního rejstříku.",
		canonical: `${SITE_URL}/firemni-udalosti`,
	});
}

const documentFamilies = [
	{
		icon: FileSignature,
		title: "Notářské zápisy",
		description:
			"Rozhodnutí valných hromad — zvýšení a snížení základního kapitálu, změny orgánů, zrušení společnosti nebo vstup do likvidace.",
	},
	{
		icon: GitMerge,
		title: "Projekty přeměn",
		description:
			"Fúze, rozdělení a převody jmění zachytíme už ve fázi projektu — typicky 1 až 4 měsíce před zápisem přeměny do rejstříku.",
	},
	{
		icon: Scale,
		title: "Stanovy a společenské smlouvy",
		description:
			"Správa společnosti: způsob jednání, omezení převoditelnosti podílů, předkupní práva a zvláštní pravidla rozdělení zisku.",
	},
	{
		icon: Network,
		title: "Zprávy o vztazích",
		description:
			"Ovládající osoby a struktura podnikatelského seskupení — přehled o skupině i poté, co se evidence skutečných majitelů uzavřela veřejnosti.",
	},
];

const steps = [
	{
		icon: Search,
		title: "Sledujeme Sbírku listin",
		description:
			"Průběžně stahujeme nové listiny z obchodního rejstříku pro více než 500 000 českých firem — notářské zápisy, projekty přeměn, stanovy i zprávy o vztazích.",
	},
	{
		icon: ScanText,
		title: "AI vytěží strukturovaná data",
		description:
			"Jazykové modely z každé listiny extrahují typ události, data, částky i protistrany. Každý údaj nese skóre spolehlivosti a odkaz na zdrojovou listinu.",
	},
	{
		icon: Bell,
		title: "Uvidíte změny dřív",
		description:
			"Události se objeví v profilu firmy i ve vašem přehledu sledovaných firem — často týdny až měsíce předtím, než se změna propíše do obchodního rejstříku.",
	},
];

export default function FiremniUdalosti() {
	return (
		<>
			{/* Header */}
			<section className="bg-gradient-to-b from-primary-50 to-background px-6 pb-16 pt-16 lg:px-8 lg:pt-24">
				<div className="mx-auto max-w-4xl text-center">
					<h1 className="text-4xl font-extrabold tracking-tight text-text sm:text-5xl">
						Firemní události z listin
					</h1>
					<p className="mx-auto mt-6 max-w-2xl text-lg text-muted leading-relaxed">
						Fúze, změny kapitálu nebo zrušení společnosti se ve Sbírce listin objeví často 1–4
						měsíce před zápisem do obchodního rejstříku. FirmIndex je z listin vytěží pomocí AI — a
						vy je uvidíte dřív, než se stanou oficiálními.
					</p>
				</div>
			</section>

			{/* Document families */}
			<section className="px-6 py-20 lg:px-8">
				<div className="mx-auto max-w-7xl">
					<div className="text-center">
						<h2 className="text-3xl font-bold tracking-tight text-text">Co sledujeme</h2>
						<p className="mx-auto mt-4 max-w-2xl text-muted">
							Čtyři typy listin, ze kterých vytěžujeme události, správu společnosti a vztahy ve
							skupině.
						</p>
					</div>
					<div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
						{documentFamilies.map((family) => (
							<div key={family.title} className="rounded-2xl bg-white p-6 shadow-card">
								<div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-50">
									<family.icon size={24} className="text-primary-600" />
								</div>
								<h3 className="text-lg font-semibold text-text">{family.title}</h3>
								<p className="mt-2 text-sm text-muted">{family.description}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* How it works */}
			<section className="bg-surface px-6 py-20 lg:px-8">
				<div className="mx-auto max-w-7xl">
					<div className="text-center">
						<h2 className="text-3xl font-bold tracking-tight text-text">Jak to funguje</h2>
						<p className="mx-auto mt-4 max-w-2xl text-muted">
							Od PDF v rejstříku ke strukturované události ve třech krocích.
						</p>
					</div>
					<div className="mt-12 grid gap-8 lg:grid-cols-3">
						{steps.map((step, index) => (
							<div key={step.title} className="rounded-2xl bg-white p-6 shadow-card">
								<div className="flex items-center gap-3">
									<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-white">
										{index + 1}
									</div>
									<step.icon size={20} className="text-primary-600" />
								</div>
								<h3 className="mt-4 text-lg font-semibold text-text">{step.title}</h3>
								<p className="mt-2 text-sm text-muted leading-relaxed">{step.description}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* CTA */}
			<section className="bg-gradient-brand px-6 py-20 lg:px-8">
				<div className="mx-auto max-w-3xl text-center">
					<h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
						Nenechte si ujít žádnou fúzi
					</h2>
					<p className="mt-4 text-lg text-primary-100">
						Zaregistrujte se zdarma a sledujte firemní události u firem, na kterých vám záleží.
						Kompletní historii a detaily odemkne plán Premium.
					</p>
					<div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
						<Link
							to="/login?mode=register"
							className="rounded-full bg-white px-8 py-3 text-sm font-semibold text-primary-600 shadow-sm hover:bg-primary-50 transition-colors"
						>
							Zaregistrujte se zdarma
						</Link>
						<Link
							to="/cenik"
							className="rounded-full border border-white/40 px-8 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
						>
							Prohlédnout ceník
						</Link>
					</div>
				</div>
			</section>
		</>
	);
}
